"use client";

import Link from "next/link";
import { JobPosting } from "@/types/careers";
import { MapPin, Clock, ArrowRight, Sparkles } from "lucide-react";

interface JobCardProps {
  job: JobPosting;
}

// Department color mapping
const departmentColors: Record<string, { bg: string; text: string; border: string }> = {
  Engineering: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-200" },
  Marketing: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-200" },
  Sales: { bg: "bg-green-50", text: "text-green-600", border: "border-green-200" },
  Design: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200" },
  Operations: { bg: "bg-teal-50", text: "text-teal-600", border: "border-teal-200" },
  default: { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200" },
};

export default function JobCard({ job }: JobCardProps) {
  const { metadata } = job;
  const colors = departmentColors[metadata.department] || departmentColors.default;

  return (
    <Link
      href={`/careers/${job.slug}`}
      className="group relative bg-white rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-500 hover:-translate-y-2"
    >
      {/* Gradient border effect */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#284D93]/20 via-transparent to-[#F6842B]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Card border */}
      <div className="absolute inset-[1px] rounded-2xl bg-white" />

      {/* Main content */}
      <div className="relative p-6 flex flex-col flex-1 border border-gray-100 rounded-2xl group-hover:border-[#284D93]/30 transition-colors duration-300">
        {/* Top accent line */}
        <div className="absolute top-0 left-6 right-6 h-1 bg-gradient-to-r from-[#284D93] to-[#F6842B] rounded-b-full transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />

        {/* Department Badge */}
        <div className="flex items-center justify-between mb-5">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border} rounded-full`}>
            <Sparkles className="w-3 h-3" />
            {metadata.department}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-[#001640] mb-4 group-hover:text-[#284D93] transition-colors duration-300 leading-tight">
          {metadata.title}
        </h3>

        {/* Meta Info */}
        <div className="space-y-3 mb-6 flex-1">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-[#284D93]/5 transition-colors duration-300">
              <MapPin className="w-4 h-4 text-[#284D93]" />
            </div>
            <span className="font-medium">{metadata.location}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-[#F6842B]/5 transition-colors duration-300">
              <Clock className="w-4 h-4 text-[#F6842B]" />
            </div>
            <span className="font-medium">{metadata.employmentType}</span>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <span className="text-sm font-semibold text-[#284D93] group-hover:text-[#F6842B] transition-colors duration-300">
            View Position
          </span>
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#284D93] text-white group-hover:bg-[#F6842B] transition-all duration-300 group-hover:scale-110">
            <ArrowRight className="w-5 h-5 transform group-hover:translate-x-0.5 transition-transform duration-300" />
          </div>
        </div>
      </div>
    </Link>
  );
}
